import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Razorpaybutton from './Razorpaybutton';
import { useDispatch, useSelector } from 'react-redux';
import { createCheckout } from '../../../redux/slices/checkoutSlice';
import { clearCart } from '../../../redux/slices/cartSlice';
import { useOfferQuote } from '../../offers/useOffer';
import {
  fetchUserProfile,
  updateUserProfile,
  saveUserAddress,
} from '../../../redux/slices/authSlice';
import axios from 'axios';


const Checkout = () => {
  const navigate  = useNavigate();
  const dispatch  = useDispatch();
  const { cart, loading, error }               = useSelector((s) => s.cart);
  const { user }                               = useSelector((s) => s.auth);
  const { checkout, loading: checkoutLoading } = useSelector((s) => s.checkout);
  const quote = useOfferQuote(cart?.products);   // offer maths (display only; the server re-prices)

  // ── Existing state ───────────────────────────────────────────────────────────
  const [checkoutId,        setCheckoutId]        = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [shippingAddress,   setShipping]          = useState({
    firstName: '', lastName: '', address: '',
    city: '', state: '', postalCode: '', country: '', phone: '',
  });

  // ── New state ────────────────────────────────────────────────────────────────
  const [recipient,       setRecipient]       = useState(null);  // 'self' | 'friend'
  const [selectedAddrIdx, setSelectedAddrIdx] = useState(null);
  const [useNewAddr,      setUseNewAddr]      = useState(false);
  const [saveAddr,        setSaveAddr]        = useState(false);
  const [savePhone,       setSavePhone]       = useState(false);

  // ── Derived ──────────────────────────────────────────────────────────────────
  const savedAddresses = user?.savedAddresses || [];
  const hasSaved       = savedAddresses.length > 0;

  // ── Fetch fresh profile on mount (to get phone + savedAddresses) ─────────────
  useEffect(() => {
    if (user?._id) dispatch(fetchUserProfile());
  }, []);

  // ── Auto-fill when "For Myself" selected ─────────────────────────────────────
  useEffect(() => {
    if (recipient !== 'self') return;

    const parts     = (user?.name || '').trim().split(' ');
    const firstName = parts[0] || '';
    const lastName  = parts.slice(1).join(' ');

    setShipping(prev => ({
      ...prev,
      firstName,
      lastName,
      phone:   user?.phone || prev.phone,
      country: prev.country || 'India',
    }));

    // Auto-select default address, or first if none is default
    const defIdx = savedAddresses.findIndex(a => a.isDefault);
    const pickIdx = defIdx >= 0 ? defIdx : hasSaved ? 0 : null;
    if (pickIdx !== null) {
      setSelectedAddrIdx(pickIdx);
      applyAddress(savedAddresses[pickIdx], firstName, lastName);
    }
  }, [recipient]);

  const applyAddress = (addr, fn, ln) => {
    setShipping(prev => ({
      ...prev,
      firstName:  fn  !== undefined ? fn  : (addr.firstName  || prev.firstName),
      lastName:   ln  !== undefined ? ln  : (addr.lastName   || prev.lastName),
      address:    addr.address    || '',
      city:       addr.city       || '',
      state:      addr.state      || '',
      postalCode: addr.postalCode || '',
      country:    addr.country    || 'India',
      phone:      addr.phone      || user?.phone || prev.phone,
    }));
  };

  const handleSelectAddr = (idx) => {
    setSelectedAddrIdx(idx);
    applyAddress(savedAddresses[idx]);
  };

  const resetRecipient = () => {
    setRecipient(null);
    setUseNewAddr(false);
    setSaveAddr(false);
    setSavePhone(false);
    setSelectedAddrIdx(null);
    setShipping({ firstName: '', lastName: '', address: '', city: '', state: '', postalCode: '', country: '', phone: '' });
  };

  // ── Debug cart ───────────────────────────────────────────────────────────────
  useEffect(() => {
    console.log('=== CART DEBUG ===');
    console.log('Full cart object:', cart);
    console.log('Cart totalPrice:', cart?.totalPrice);
    console.log('Cart products:', cart?.products);
    if (cart?.products) {
      console.log('Individual product prices:');
      cart.products.forEach((product, index) => {
        console.log(`Product ${index}:`, {
          name: product.name, price: product.price,
          quantity: product.quantity, subtotal: product.price * product.quantity,
        });
      });
      const manualTotal = cart.products.reduce((sum, item) => sum + (Number(item.price) * Number(item.quantity)), 0);
      console.log('Manual calculated total:', manualTotal);
    }
  }, [cart]);

  // ── Total calculation ────────────────────────────────────────────────────────
  const calculateTotalPrice = () => {
    if (!cart || !cart.products || cart.products.length === 0) return 0;
    const total = cart.products.reduce((sum, item) => sum + (Number(item.price) || 0) * (Number(item.quantity) || 0), 0);
    console.log('Calculated total:', total);
    return total;
  };

  const correctTotal = calculateTotalPrice();                          // before any offer
  const estimatedTotal = Math.max(correctTotal - quote.discount, 0);   // what the live offer says
  // Once the checkout exists, the SERVER's total is the truth (it re-prices from the database)
  const serverTotal = checkoutId ? Number(checkout?.checkout?.totalPrice ?? checkout?.totalPrice) : NaN;
  const displayTotal = Number.isFinite(serverTotal)
    ? serverTotal
    : (correctTotal > 0 ? estimatedTotal : (cart?.totalPrice || 0));

  // ── Redirect if cart empty ───────────────────────────────────────────────────
  useEffect(() => {
    if (!cart || !cart.products || cart.products.length === 0) {
      if (!checkoutId && !paymentProcessing) navigate('/');
    }
  }, [cart, checkoutId, paymentProcessing, navigate]);

  // ── Create checkout ──────────────────────────────────────────────────────────
  const handleCreateCheckout = async (e) => {
    e.preventDefault();

    // For "Myself", name comes from the profile and has no input field, so a single-word
    // name (empty lastName) must not block checkout. Friend orders still require both names.
    const requiredFields = recipient === 'self'
      ? ['address', 'city', 'state', 'postalCode', 'country', 'phone']
      : ['firstName', 'lastName', 'address', 'city', 'state', 'postalCode', 'country', 'phone'];
    if (requiredFields.some(f => !shippingAddress[f]?.trim())) {
      alert('Please fill in all required fields');
      return;
    }
    if (!/^[\+]?[1-9][\d]{9,15}$/.test(shippingAddress.phone.replace(/[\s\-\(\)]/g, ''))) {
      alert('Please enter a valid phone number');
      return;
    }

    // ── Save phone to profile (fire and forget — won't block checkout) ──────
    if (recipient === 'self' && savePhone && shippingAddress.phone) {
      dispatch(updateUserProfile({ phone: shippingAddress.phone })).catch(() => {});
    }

    // ── Save new address to profile (fire and forget) ───────────────────────
    if (recipient === 'self' && saveAddr) {
      dispatch(saveUserAddress({
        label:      'Home',
        firstName:  shippingAddress.firstName,
        lastName:   shippingAddress.lastName,
        address:    shippingAddress.address,
        city:       shippingAddress.city,
        state:      shippingAddress.state,
        postalCode: shippingAddress.postalCode,
        country:    shippingAddress.country,
        phone:      shippingAddress.phone,
      })).catch(() => {});
    }

    console.log('Creating checkout with correct total:', displayTotal);

    if (cart && cart.products && cart.products.length > 0) {
      try {
        const sanitizedAddress = {
          firstName:  shippingAddress.firstName.trim(),
          lastName:   shippingAddress.lastName.trim(),
          address:    shippingAddress.address.trim(),
          city:       shippingAddress.city.trim(),
          state:      shippingAddress.state.trim(),
          postalCode: shippingAddress.postalCode.trim(),
          country:    shippingAddress.country.trim(),
          phone:      shippingAddress.phone.trim(),
        };

        const result = await dispatch(createCheckout({
          checkoutItems:   cart.products,
          shippingAddress: sanitizedAddress,
          paymentMethod:   'Online',
          totalPrice:      displayTotal,
        }));

        if (createCheckout.fulfilled.match(result)) {
          console.log('✅ Checkout created successfully:', result.payload);
          setCheckoutId(result.payload?._id);
        } else {
          console.error('❌ Checkout creation failed:', result);
          alert('Failed to create checkout session');
        }
      } catch (error) {
        console.error('❌ Checkout creation error:', error);
        alert('Failed to create checkout session');
      }
    } else {
      alert('No items in cart');
    }
  };

  // ── Payment success ──────────────────────────────────────────────────────────
  const handlePaymentSuccess = async (paymentDetails) => {
    console.log('🎉 Payment received from Razorpay, starting verification...');
    console.log('💳 Payment details:', paymentDetails);
    setPaymentProcessing(true);

    try {
      const token = localStorage.getItem('userToken');
      if (!token) {
        alert('Session expired. Please login again.');
        navigate('/login?redirect=checkout');
        return;
      }

      console.log('⏳ Step 1: Verifying payment signature...');
      const verificationResponse = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/api/checkout/${checkoutId}/verify-payment`,
        {
          paymentId: paymentDetails.paymentId,
          orderId:   paymentDetails.orderId,
          signature: paymentDetails.signature,
          amount:    paymentDetails.amount,
          currency:  paymentDetails.currency,
        },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      if (!verificationResponse.data.success) throw new Error('Payment verification failed');
      console.log('✅ Step 1 completed - Payment verified successfully');

      console.log('⏳ Step 2: Finalizing checkout to create order...');
      const finalizeResponse = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/api/checkout/${checkoutId}/finalize`,
        {},
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      console.log('✅ Step 2 completed - Order created:', finalizeResponse.data);

      console.log('⏳ Step 3: Clearing cart...');
      dispatch(clearCart());
      console.log('✅ Cart cleared from Redux');

      console.log('🚀 Navigating to order confirmation page...');
      setTimeout(() => {
        navigate('/order-confirmation', {
          state: {
            orderId: finalizeResponse.data._id,
            message: 'Order placed successfully!',
            verified: true,
          },
        });
      }, 500);

    } catch (error) {
      console.error('❌ Error in handlePaymentSuccess:', error);
      console.error('📋 Error details:', {
        message: error.message,
        response: error.response?.data,
        status:   error.response?.status,
      });

      setPaymentProcessing(false);

      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
        localStorage.removeItem('userToken');
        navigate('/login?redirect=checkout');
        return;
      }

      if (error.response?.data?.message) {
        alert(`Error: ${error.response.data.message}`);
      } else {
        alert('Payment processing failed. Please contact support if amount was deducted.');
      }
    }
  };

  // ── Payment error ────────────────────────────────────────────────────────────
  const handlePaymentError = (error) => {
    console.error('💥 Payment Error from Razorpay:', error);
    setPaymentProcessing(false);
    if (error?.code === 'BAD_REQUEST_ERROR') {
      alert('Payment failed: ' + (error.description || 'Please try again'));
    } else {
      alert('Payment was cancelled or failed. No amount was charged.');
    }
  };

  // ── Customer details for Razorpay ────────────────────────────────────────────
  const customerDetails = {
    name:       `${shippingAddress.firstName} ${shippingAddress.lastName}`.trim(),
    email:      user?.email || '',
    phone:      shippingAddress.phone,
    mobile:     shippingAddress.phone,
    address:    shippingAddress.address,
    city:       shippingAddress.city,
    state:      shippingAddress.state,
    postalCode: shippingAddress.postalCode,
    country:    shippingAddress.country,
  };

  // ── Shared address form fields ───────────────────────────────────────────────
  const renderAddressFields = () => (
    <>
      <div className="co-field">
        <label className="co-label">Address <span className="co-req">*</span></label>
        <input
          type="text"
          value={shippingAddress.address}
          onChange={(e) => setShipping({ ...shippingAddress, address: e.target.value })}
          placeholder="Street address, apartment, floor..."
          required maxLength={200}
          className="co-input"
        />
      </div>

      <div className="co-form-row">
        <div className="co-field">
          <label className="co-label">City <span className="co-req">*</span></label>
          <input
            type="text"
            value={shippingAddress.city}
            onChange={(e) => setShipping({ ...shippingAddress, city: e.target.value })}
            placeholder="City"
            required maxLength={50}
            className="co-input"
          />
        </div>
        <div className="co-field">
          <label className="co-label">State <span className="co-req">*</span></label>
          <input
            type="text"
            value={shippingAddress.state}
            onChange={(e) => setShipping({ ...shippingAddress, state: e.target.value })}
            placeholder="State"
            required maxLength={50}
            className="co-input"
          />
        </div>
      </div>

      <div className="co-form-row">
        <div className="co-field">
          <label className="co-label">PIN Code <span className="co-req">*</span></label>
          <input
            type="text"
            value={shippingAddress.postalCode}
            onChange={(e) => setShipping({ ...shippingAddress, postalCode: e.target.value })}
            placeholder="PIN code"
            required maxLength={10}
            className="co-input"
          />
        </div>
        <div className="co-field">
          <label className="co-label">Country <span className="co-req">*</span></label>
          <input
            type="text"
            value={shippingAddress.country}
            onChange={(e) => setShipping({ ...shippingAddress, country: e.target.value })}
            placeholder="Country"
            required maxLength={50}
            className="co-input"
          />
        </div>
      </div>
    </>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{checkoutStyles}</style>

      <div className="co-root">

        {/* ── PAYMENT PROCESSING OVERLAY ── */}
        {paymentProcessing && (
          <div className="co-overlay">
            <div className="co-overlay-box">
              <div className="co-overlay-spinner" />
              <span className="co-overlay-title">Processing Payment</span>
              <span className="co-overlay-sub">Please do not close this window</span>
            </div>
          </div>
        )}

        {/* ── HEADER ── */}
        <div className="co-header">
          <span className="co-eyebrow">Secure Checkout</span>
          <h1 className="co-title">Check<span className="co-title-bold">out</span></h1>
        </div>
        <div className="co-rule-wrap"><hr className="co-hr" /></div>

        {/* ── TWO COLUMN GRID ── */}
        <div className="co-grid">

          {/* ── ORDER SUMMARY ── */}
          <div className="co-panel">
            <div className="co-panel-heading">
              <span className="co-panel-eyebrow">Your Order</span>
              <h2 className="co-panel-title">Order <span className="co-panel-bold">Summary</span></h2>
            </div>
            <div className="co-panel-rule"><hr className="co-hr" /></div>

            <div className="co-items">
              {cart.products.map((product, index) => (
                <div key={index} className="co-item">
                  <img src={product.image} alt={product.name} className="co-item-img" />
                  <div className="co-item-info">
                    <span className="co-item-name">{product.name}</span>
                    <span className="co-item-meta">
                      {product.size && product.size}
                      {product.size && product.color && ' · '}
                      {product.color && product.color}
                    </span>
                    <span className="co-item-qty">Qty · {product.quantity}</span>
                    {quote.freeQtyFor(product) > 0 && (
                      <span className="co-item-free">{quote.freeQtyFor(product)} Free</span>
                    )}
                  </div>
                  <div className="co-item-price">
                    <span className="co-item-total">₹{(product.price * product.quantity).toLocaleString()}</span>
                    {product.quantity > 1 && (
                      <span className="co-item-unit">₹{Number(product.price).toLocaleString()} each</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="co-summary-total">
              <div className="co-panel-rule" style={{ marginBottom: '16px' }}><hr className="co-hr" /></div>
              {quote.applied && (
                <>
                  <div className="co-total-row co-sub-row">
                    <span className="co-sub-label">Subtotal</span>
                    <span className="co-sub-value">₹{correctTotal.toLocaleString()}</span>
                  </div>
                  <div className="co-total-row co-sub-row">
                    <span className="co-sub-label">{quote.offer.name}</span>
                    <span className="co-sub-value">— ₹{quote.discount.toLocaleString()}</span>
                  </div>
                </>
              )}
              <div className="co-total-row">
                <span className="co-total-label">Total</span>
                <span className="co-total-price">₹{displayTotal.toLocaleString()}</span>
              </div>
              {cart.totalPrice !== correctTotal && (
                <p className="co-total-note">Recalculated from cart items</p>
              )}
              {Number.isFinite(serverTotal) && Math.abs(serverTotal - estimatedTotal) > 0.5 && (
                <p className="co-total-note">Price updated to match the current offer</p>
              )}
              <div className="co-free-ship">
                <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Free Shipping · Secure Payment
              </div>
            </div>
          </div>

          {/* ── SHIPPING PANEL ── */}
          <div className="co-panel">
            <div className="co-panel-heading">
              <span className="co-panel-eyebrow">Delivery</span>
              <h2 className="co-panel-title">Shipping <span className="co-panel-bold">Information</span></h2>
            </div>
            <div className="co-panel-rule"><hr className="co-hr" /></div>

            {!checkoutId ? (
              <>
                {/* ── STEP 0: WHO IS THIS FOR? ────────────────────────── */}
                {!recipient && (
                  <div className="co-recipient">
                    <span className="co-recipient-label">This order is for</span>
                    <div className="co-recipient-btns">
                      <button
                        type="button"
                        className="co-recipient-btn"
                        onClick={() => setRecipient('self')}
                      >
                        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ marginBottom: 6 }}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span>Myself</span>
                        <span className="co-recipient-sub">Use my saved info</span>
                      </button>
                      <button
                        type="button"
                        className="co-recipient-btn"
                        onClick={() => setRecipient('friend')}
                      >
                        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ marginBottom: 6 }}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>A Friend</span>
                        <span className="co-recipient-sub">Enter their details</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* ── STEP 1: FORM ─────────────────────────────────────── */}
                {recipient && (
                  <form onSubmit={handleCreateCheckout} className="co-form">

                    {/* Back button */}
                    <button type="button" className="co-back-btn" onClick={resetRecipient}>
                      ← Change recipient
                    </button>

                    {/* ────────── FOR MYSELF ────────── */}
                    {recipient === 'self' && (
                      <>
                        {/* Readonly: Name + Email from profile */}
                        <div className="co-readonly-block">
                          <div className="co-readonly-row">
                            <span className="co-readonly-label">Name</span>
                            <span className="co-readonly-value">{user?.name || '—'}</span>
                          </div>
                          <div className="co-readonly-row">
                            <span className="co-readonly-label">Email</span>
                            <span className="co-readonly-value">{user?.email || '—'}</span>
                          </div>
                        </div>

                        {/* Phone — editable, pre-filled from profile */}
                        <div className="co-field">
                          <label className="co-label">Mobile <span className="co-req">*</span></label>
                          <input
                            type="tel"
                            value={shippingAddress.phone}
                            onChange={(e) => setShipping({ ...shippingAddress, phone: e.target.value })}
                            placeholder="10-digit mobile number"
                            required maxLength={15}
                            className="co-input"
                          />
                        </div>

                        {/* Save phone — only if profile phone is empty */}
                        {!user?.phone && (
                          <label className="co-save-toggle">
                            <input
                              type="checkbox"
                              className="co-save-checkbox"
                              checked={savePhone}
                              onChange={(e) => setSavePhone(e.target.checked)}
                            />
                            <span className="co-save-label">Save phone number to my profile</span>
                          </label>
                        )}

                        {/* ── Address: saved cards ── */}
                        {hasSaved && !useNewAddr && (
                          <>
                            <span className="co-section-label">Delivery Address</span>
                            <div className="co-addr-list">
                              {savedAddresses.map((addr, idx) => (
                                <div
                                  key={idx}
                                  className={`co-addr-card ${selectedAddrIdx === idx ? 'co-addr-card--active' : ''}`}
                                  onClick={() => handleSelectAddr(idx)}
                                >
                                  <span className="co-addr-card-badge">{addr.label || 'Home'}</span>
                                  <div className="co-addr-card-name">{addr.firstName} {addr.lastName}</div>
                                  <div className="co-addr-card-line">{addr.address}</div>
                                  <div className="co-addr-card-line">{addr.city}, {addr.state} · {addr.postalCode}</div>
                                </div>
                              ))}
                              <button
                                type="button"
                                className="co-new-addr-btn"
                                onClick={() => { setUseNewAddr(true); setSaveAddr(false); }}
                              >
                                + Use a different address
                              </button>
                            </div>
                          </>
                        )}

                        {/* ── Address: form (no saved, or useNewAddr) ── */}
                        {(!hasSaved || useNewAddr) && (
                          <>
                            {useNewAddr && hasSaved && (
                              <button
                                type="button"
                                className="co-new-addr-btn"
                                style={{ marginBottom: 8 }}
                                onClick={() => { setUseNewAddr(false); handleSelectAddr(selectedAddrIdx ?? 0); }}
                              >
                                ← Back to saved addresses
                              </button>
                            )}

                            <span className="co-section-label">
                              {hasSaved ? 'New Delivery Address' : 'Delivery Address'}
                            </span>

                            {renderAddressFields()}

                            <label className="co-save-toggle">
                              <input
                                type="checkbox"
                                className="co-save-checkbox"
                                checked={saveAddr}
                                onChange={(e) => setSaveAddr(e.target.checked)}
                              />
                              <span className="co-save-label">Save this address to my profile</span>
                            </label>
                          </>
                        )}
                      </>
                    )}

                    {/* ────────── FOR A FRIEND ────────── */}
                    {recipient === 'friend' && (
                      <>
                        <div className="co-form-row">
                          <div className="co-field">
                            <label className="co-label">First Name <span className="co-req">*</span></label>
                            <input
                              type="text"
                              value={shippingAddress.firstName}
                              onChange={(e) => setShipping({ ...shippingAddress, firstName: e.target.value })}
                              placeholder="First name"
                              required maxLength={50}
                              className="co-input"
                            />
                          </div>
                          <div className="co-field">
                            <label className="co-label">Last Name <span className="co-req">*</span></label>
                            <input
                              type="text"
                              value={shippingAddress.lastName}
                              onChange={(e) => setShipping({ ...shippingAddress, lastName: e.target.value })}
                              placeholder="Last name"
                              required maxLength={50}
                              className="co-input"
                            />
                          </div>
                        </div>

                        {renderAddressFields()}

                        <div className="co-field">
                          <label className="co-label">Phone <span className="co-req">*</span></label>
                          <input
                            type="tel"
                            value={shippingAddress.phone}
                            onChange={(e) => setShipping({ ...shippingAddress, phone: e.target.value })}
                            placeholder="Friend's mobile number"
                            required maxLength={15}
                            className="co-input"
                          />
                        </div>
                      </>
                    )}

                    <button type="submit" disabled={checkoutLoading || !recipient} className="co-submit">
                      {checkoutLoading ? 'Creating Session...' : 'Proceed to Payment'}
                    </button>

                  </form>
                )}
              </>
            ) : (
              /* ── CONFIRMED ADDRESS ── */
              <div className="co-confirmed">
                <div className="co-confirmed-badge">
                  <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Shipping Details Confirmed
                </div>

                <div className="co-address-block">
                  {[
                    ['Name',        `${shippingAddress.firstName} ${shippingAddress.lastName}`],
                    ['Address',     shippingAddress.address],
                    ['City',        `${shippingAddress.city}, ${shippingAddress.state}`],
                    ['Postal Code', shippingAddress.postalCode],
                    ['Country',     shippingAddress.country],
                    ['Phone',       shippingAddress.phone],
                  ].map(([label, value]) => (
                    <div key={label} className="co-address-row">
                      <span className="co-address-label">{label}</span>
                      <span className="co-address-value">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── PAYMENT ── */}
            {checkoutId && (
              <div className="co-payment-wrap">
                <div className="co-panel-rule" style={{ margin: '24px 0' }}><hr className="co-hr" /></div>

                <div className="co-payment-heading">
                  <span className="co-panel-eyebrow">Payment</span>
                  <h3 className="co-payment-title">Complete <span className="co-panel-bold">Payment</span></h3>
                </div>

                <div className="co-amount-box">
                  <span className="co-amount-label">Amount Due</span>
                  <span className="co-amount-value">₹{displayTotal.toLocaleString()}</span>
                </div>

                <Razorpaybutton
                  totalPrice={displayTotal}
                  customerDetails={customerDetails}
                  checkoutId={checkoutId}
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                />

                <div className="co-secure-note">
                  <svg width="11" height="11" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  Secure payment · 256-bit encryption · Powered by Razorpay
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </>
  );
};

/* ── STYLES ── */
const checkoutStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@200;300;400;500;600&display=swap');

  @keyframes co-spin { to { transform: rotate(360deg); } }

  .co-root {
    background: #f8f8f8;
    min-height: 100vh;
    font-family: 'Exo 2', sans-serif;
    padding: 64px 80px 96px;
    max-width: 1400px;
    margin: 0 auto;
    box-sizing: border-box;
  }

  /* ── OVERLAY ── */
  .co-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.6);
    display: flex; align-items: center; justify-content: center;
    z-index: 50;
  }
  .co-overlay-box {
    background: #f8f8f8; padding: 48px 56px;
    display: flex; flex-direction: column; align-items: center;
    gap: 16px; min-width: 280px;
  }
  .co-overlay-spinner {
    width: 32px; height: 32px;
    border: 2px solid black; border-top-color: #000;
    border-radius: 50%; animation: co-spin 0.8s linear infinite;
  }
  .co-overlay-title {
    font-size: 10px; font-weight: 600;
    letter-spacing: 0.4em; text-transform: uppercase; color: #000;
  }
  .co-overlay-sub {
    font-size: 9px; font-weight: 300;
    letter-spacing: 0.2em; color: black; text-transform: uppercase;
  }

  /* ── HEADER ── */
  .co-header { margin-bottom: 32px; }
  .co-eyebrow {
    display: flex; align-items: center; gap: 12px;
    font-size: 9px; font-weight: 400; letter-spacing: 0.5em;
    text-transform: uppercase; color: black; margin-bottom: 12px;
  }
  .co-eyebrow::before {
    content: ''; width: 24px; height: 1px;
    background: rgba(0,0,0,0.2); display: block; flex-shrink: 0;
  }
  .co-title {
    font-size: clamp(32px, 4.5vw, 56px); font-weight: 200; color: #000;
    letter-spacing: 0.08em; text-transform: uppercase; line-height: 1; margin: 0;
  }
  .co-title-bold { font-weight: 600; }

  /* Rules */
  .co-rule-wrap { margin-bottom: 48px; }
  .co-hr { border: none; border-top: 1px solid black; margin: 0; }

  /* ── GRID ── */
  .co-grid {
    display: grid; grid-template-columns: 1fr 1fr;
    gap: 64px; align-items: start;
  }

  /* ── PANEL ── */
  .co-panel-heading { margin-bottom: 20px; }
  .co-panel-eyebrow {
    display: flex; align-items: center; gap: 10px;
    font-size: 8px; font-weight: 400; letter-spacing: 0.45em;
    text-transform: uppercase; color: black; margin-bottom: 10px;
  }
  .co-panel-eyebrow::before {
    content: ''; width: 18px; height: 1px;
    background: rgba(0,0,0,0.15); display: block; flex-shrink: 0;
  }
  .co-panel-title {
    font-size: clamp(20px, 2.5vw, 32px); font-weight: 200; color: #000;
    letter-spacing: 0.08em; text-transform: uppercase; line-height: 1; margin: 0;
  }
  .co-panel-bold { font-weight: 600; }
  .co-panel-rule { margin-bottom: 24px; }

  /* ── ORDER ITEMS ── */
  .co-items { display: flex; flex-direction: column; gap: 0; }
  .co-item {
    display: flex; gap: 14px; padding: 18px 0;
    border-bottom: 1px solid black; align-items: flex-start;
  }
  .co-item:last-child { border-bottom: none; }
  .co-item-img { width: 72px; height: 86px; object-fit: cover; flex-shrink: 0; display: block; }
  .co-item-info { flex: 1; display: flex; flex-direction: column; gap: 4px; min-width: 0; }
  .co-item-name {
    font-size: 10px; font-weight: 500; letter-spacing: 0.2em;
    text-transform: uppercase; color: #000; line-height: 1.4;
  }
  .co-item-meta {
    font-size: 9px; font-weight: 300; letter-spacing: 0.18em;
    text-transform: uppercase; color: black;
  }
  .co-item-qty {
    font-size: 8px; font-weight: 400; letter-spacing: 0.25em;
    text-transform: uppercase; color: black; margin-top: 4px;
  }
  .co-item-price { display: flex; flex-direction: column; align-items: flex-end; gap: 3px; flex-shrink: 0; }
  .co-item-total { font-size: 13px; font-weight: 500; letter-spacing: 0.06em; color: #000; }
  .co-item-unit { font-size: 8px; font-weight: 300; letter-spacing: 0.15em; color: black; }

  /* Summary total */
  .co-summary-total { padding-top: 8px; }
  .co-total-row { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 8px; }
  .co-total-label { font-size: 9px; font-weight: 500; letter-spacing: 0.4em; text-transform: uppercase; color: black; }
  .co-total-price { font-size: 22px; font-weight: 300; letter-spacing: 0.04em; color: #000; }
  .co-item-free { align-self: flex-start; background: #000; color: #fff; font-size: 7px; font-weight: 600; letter-spacing: 0.25em; text-transform: uppercase; padding: 3px 6px; margin-top: 2px; }
  .co-sub-row { margin-bottom: 6px; }
  .co-sub-label { font-size: 8px; font-weight: 400; letter-spacing: 0.3em; text-transform: uppercase; color: black; }
  .co-sub-value { font-size: 10px; font-weight: 400; letter-spacing: 0.1em; color: black; }
  .co-total-note { font-size: 8px; font-weight: 300; letter-spacing: 0.2em; color: black; text-transform: uppercase; margin-bottom: 12px; }
  .co-free-ship {
    display: flex; align-items: center; gap: 6px;
    font-size: 8px; font-weight: 400; letter-spacing: 0.25em;
    text-transform: uppercase; color: black; margin-top: 12px;
  }

  /* ── RECIPIENT STEP ── */
  .co-recipient { display: flex; flex-direction: column; gap: 16px; }
  .co-recipient-label {
    font-size: 8px; font-weight: 500; letter-spacing: 0.45em;
    text-transform: uppercase; color: rgba(0,0,0,0.5);
  }
  .co-recipient-btns { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .co-recipient-btn {
    display: flex; flex-direction: column; align-items: center; gap: 2px;
    padding: 24px 16px; background: transparent;
    border: 1px solid rgba(0,0,0,0.12); cursor: pointer;
    font-family: 'Exo 2', sans-serif; transition: border-color 0.2s, background 0.2s;
  }
  .co-recipient-btn span:first-of-type {
    font-size: 10px; font-weight: 600; letter-spacing: 0.25em;
    text-transform: uppercase; color: #000;
  }
  .co-recipient-sub {
    font-size: 8px; font-weight: 300; letter-spacing: 0.15em;
    text-transform: uppercase; color: rgba(0,0,0,0.4);
  }
  .co-recipient-btn:hover { border-color: #000; background: rgba(0,0,0,0.02); }

  /* ── BACK BUTTON ── */
  .co-back-btn {
    background: none; border: none; padding: 0;
    font-family: 'Exo 2', sans-serif;
    font-size: 8px; font-weight: 400; letter-spacing: 0.3em;
    text-transform: uppercase; color: rgba(0,0,0,0.35);
    cursor: pointer; margin-bottom: 24px; padding-bottom: 2px;
    border-bottom: 1px solid rgba(0,0,0,0.1);
    transition: color 0.2s; display: inline-block;
  }
  .co-back-btn:hover { color: #000; }

  /* ── READONLY BLOCK ── */
  .co-readonly-block { border-top: 1px solid rgba(0,0,0,0.08); }
  .co-readonly-row {
    display: flex; justify-content: space-between; align-items: baseline;
    padding: 10px 0; border-bottom: 1px solid rgba(0,0,0,0.08);
  }
  .co-readonly-label {
    font-size: 8px; font-weight: 500; letter-spacing: 0.4em;
    text-transform: uppercase; color: rgba(0,0,0,0.35); flex-shrink: 0;
  }
  .co-readonly-value {
    font-size: 12px; font-weight: 300; letter-spacing: 0.06em; color: #000; text-align: right;
  }

  /* ── SECTION LABEL ── */
  .co-section-label {
    display: block; font-size: 8px; font-weight: 500;
    letter-spacing: 0.45em; text-transform: uppercase; color: rgba(0,0,0,0.5);
    margin-top: 4px; margin-bottom: 10px;
  }

  /* ── SAVED ADDRESS CARDS ── */
  .co-addr-list { display: flex; flex-direction: column; gap: 6px; }
  .co-addr-card {
    padding: 14px 16px; border: 1px solid rgba(0,0,0,0.1);
    cursor: pointer; transition: border-color 0.2s, background 0.2s;
  }
  .co-addr-card:hover { border-color: rgba(0,0,0,0.4); }
  .co-addr-card--active { border-color: #000; background: #000; }
  .co-addr-card-badge {
    display: inline-block; font-size: 7px; font-weight: 600;
    letter-spacing: 0.25em; text-transform: uppercase;
    color: rgba(0,0,0,0.35); border: 1px solid rgba(0,0,0,0.12);
    padding: 2px 7px; margin-bottom: 8px;
  }
  .co-addr-card--active .co-addr-card-badge { color: rgba(255,255,255,0.4); border-color: rgba(255,255,255,0.15); }
  .co-addr-card-name {
    font-size: 9px; font-weight: 600; letter-spacing: 0.15em;
    text-transform: uppercase; color: #000; margin-bottom: 4px;
  }
  .co-addr-card--active .co-addr-card-name { color: #fff; }
  .co-addr-card-line {
    font-size: 10px; font-weight: 300; letter-spacing: 0.05em;
    color: rgba(0,0,0,0.5); line-height: 1.6;
  }
  .co-addr-card--active .co-addr-card-line { color: rgba(255,255,255,0.55); }

  /* ── NEW ADDRESS / BACK LINK ── */
  .co-new-addr-btn {
    background: none; border: none; padding: 12px 0;
    font-family: 'Exo 2', sans-serif;
    font-size: 8px; font-weight: 500; letter-spacing: 0.3em;
    text-transform: uppercase; color: rgba(0,0,0,0.4);
    cursor: pointer; text-align: left;
    border-bottom: 1px solid rgba(0,0,0,0.08);
    transition: color 0.2s; display: block; width: 100%;
  }
  .co-new-addr-btn:hover { color: #000; }

  /* ── SAVE TOGGLE ── */
  .co-save-toggle {
    display: flex; align-items: center; gap: 10px;
    cursor: pointer; padding: 4px 0;
  }
  .co-save-checkbox {
    width: 14px; height: 14px; cursor: pointer;
    accent-color: #000; flex-shrink: 0;
    border: 1px solid rgba(0,0,0,0.3);
  }
  .co-save-label {
    font-size: 8.5px; font-weight: 400; letter-spacing: 0.25em;
    text-transform: uppercase; color: rgba(0,0,0,0.45); cursor: pointer;
  }

  /* ── FORM ── */
  .co-form { display: flex; flex-direction: column; gap: 22px; }
  .co-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
  .co-field { display: flex; flex-direction: column; gap: 8px; }
  .co-label { font-size: 8px; font-weight: 500; letter-spacing: 0.45em; text-transform: uppercase; color: black; }
  .co-req { color: black; }
  .co-input {
    background: transparent; border: none; border-bottom: 1px solid black;
    outline: none; font-family: 'Exo 2', sans-serif;
    font-size: 13px; font-weight: 300; letter-spacing: 0.06em; color: #000;
    padding: 10px 0; width: 100%; transition: border-color 0.2s ease;
    border-radius: 0; -webkit-appearance: none;
  }
  .co-input::placeholder { color: rgba(0,0,0,0.3); }
  .co-input:focus { border-bottom-color: #000; }
  .co-submit {
    width: 100%; background: #000; color: #fff; border: none;
    font-family: 'Exo 2', sans-serif; font-size: 10px; font-weight: 600;
    letter-spacing: 0.4em; text-transform: uppercase; padding: 18px;
    cursor: pointer; transition: background 0.2s ease; margin-top: 8px;
  }
  .co-submit:hover:not(:disabled) { background: #222; }
  .co-submit:disabled { background: rgba(0,0,0,0.4); cursor: not-allowed; }

  /* ── CONFIRMED ADDRESS ── */
  .co-confirmed { margin-bottom: 0; }
  .co-confirmed-badge {
    display: inline-flex; align-items: center; gap: 8px;
    font-size: 9px; font-weight: 500; letter-spacing: 0.3em;
    text-transform: uppercase; color: black; margin-bottom: 20px;
    padding: 8px 12px; border: 1px solid black;
  }
  .co-address-block { display: flex; flex-direction: column; gap: 0; border-top: 1px solid black; }
  .co-address-row {
    display: flex; justify-content: space-between; align-items: baseline;
    padding: 11px 0; border-bottom: 1px solid black; gap: 16px;
  }
  .co-address-row:last-child { border-bottom: none; }
  .co-address-label { font-size: 8px; font-weight: 500; letter-spacing: 0.4em; text-transform: uppercase; color: black; flex-shrink: 0; }
  .co-address-value { font-size: 12px; font-weight: 300; letter-spacing: 0.06em; color: #000; text-align: right; }

  /* ── PAYMENT SECTION ── */
  .co-payment-heading { margin-bottom: 16px; }
  .co-payment-title { font-size: clamp(16px, 2vw, 24px); font-weight: 200; color: #000; letter-spacing: 0.08em; text-transform: uppercase; line-height: 1; margin: 0; }
  .co-amount-box {
    display: flex; justify-content: space-between; align-items: baseline;
    padding: 16px 0; border-top: 1px solid black; border-bottom: 1px solid black; margin-bottom: 20px;
  }
  .co-amount-label { font-size: 8px; font-weight: 500; letter-spacing: 0.4em; text-transform: uppercase; color: black; }
  .co-amount-value { font-size: 22px; font-weight: 300; letter-spacing: 0.04em; color: #000; }
  .co-secure-note {
    display: flex; align-items: center; gap: 6px;
    font-size: 8px; font-weight: 300; letter-spacing: 0.2em;
    text-transform: uppercase; color: black; margin-top: 14px; justify-content: center;
  }

  /* ── RESPONSIVE ── */
  @media (max-width: 1024px) {
    .co-root { padding: 48px 40px 80px; }
    .co-grid { gap: 40px; }
  }
  @media (max-width: 768px) {
    .co-root { padding: 32px 20px 64px; }
    .co-grid { grid-template-columns: 1fr; gap: 48px; }
    .co-form-row { grid-template-columns: 1fr; gap: 18px; }
    .co-recipient-btns { grid-template-columns: 1fr; }
  }
  @media (max-width: 360px) {
    .co-root { padding: 24px 14px 48px; }
  }
`;

export default Checkout;
